/**
 * Step 4 — Riepilogo ordine MSSP + conferma.
 * Niente pagamento, niente reCAPTCHA.
 */
import React from 'react';
import { ClipboardCheck, Shield, Wifi, Eye, Monitor, Package } from 'lucide-react';

export default function StepRiepilogoMSSP({
  aziendaData,
  isCreatingNew,
  ordineData,
  listinoData,
  errors,
}) {
  const prodottoId = ordineData.listino_prodotto_base;
  const prodotto = listinoData?.find(p => p.id === parseInt(prodottoId) || p.id === prodottoId) || null;
  const isBundle = prodotto?.tipo_prodotto === 'BUNDLE';

  // Calcolo prezzo
  const calcolaPrezzo = () => {
    if (!prodotto) return 0;
    const numEP = parseInt(ordineData.numero_endpoint) || 0;
    let totale = parseFloat(prodotto.prezzo_base) || 0;

    if (isBundle) {
      // Scalabilità EP bundle
      if (prodotto.scalabile && prodotto.numero_endpoint > 0 && numEP > prodotto.numero_endpoint) {
        const epExtra = numEP - prodotto.numero_endpoint;
        const blocchi = Math.ceil(epExtra / (prodotto.incremento_scalabilita || 10));
        totale += blocchi * parseFloat(prodotto.prezzo_scalabilita || 0);
      }
      // Extra awareness
      if (ordineData.awareness_enabled && ordineData.awareness_seats) {
        const seats = parseInt(ordineData.awareness_seats);
        const seatsIncluse = prodotto.numero_awareness_seats || 10;
        if (seats > seatsIncluse) {
          const awrProd = listinoData?.find(p => p.tipo_prodotto === 'AWARENESS' && p.attivo);
          if (awrProd?.scalabile) {
            const blocchi = Math.ceil((seats - seatsIncluse) / (awrProd.incremento_scalabilita || 10));
            totale += blocchi * parseFloat(awrProd.prezzo_scalabilita || awrProd.prezzo_base);
          }
        }
      }
      // Extra gettoni MSSP
      if (ordineData.servizio_mssp_monitoring && ordineData.gettoni_totali) {
        const gettoni = parseInt(ordineData.gettoni_totali);
        const gettoniInclusi = prodotto.numero_gettoni || 20;
        if (gettoni > gettoniInclusi) {
          const msspProd = listinoData?.find(p => p.tipo_prodotto === 'MSSP_MONITORING' && p.attivo);
          if (msspProd) {
            const prezzoPerGett = parseFloat(msspProd.prezzo_base) / (msspProd.numero_gettoni || 20);
            totale += (gettoni - gettoniInclusi) * prezzoPerGett;
          }
        }
      }
    } else {
      // Prodotto singolo
      const epBase = prodotto.numero_endpoint || 10;
      if (prodotto.scalabile && numEP > epBase) {
        const blocchi = Math.ceil((numEP - epBase) / (prodotto.incremento_scalabilita || 10));
        totale += blocchi * parseFloat(prodotto.prezzo_scalabilita || 0);
      }
      // NS
      if (ordineData.offerta_sorveglianza_rete) {
        const nsProd = listinoData?.find(p => p.tipo_prodotto === 'NETWORK_SURVEILLANCE' && p.attivo);
        if (nsProd) totale += parseFloat(nsProd.prezzo_base);
      }
      // Awareness
      if (ordineData.awareness_enabled && ordineData.awareness_seats) {
        const awrProd = listinoData?.find(p => p.tipo_prodotto === 'AWARENESS' && p.attivo);
        if (awrProd) {
          const seatsBase = awrProd.numero_awareness_seats || 10;
          totale += parseFloat(awrProd.prezzo_base);
          const seats = parseInt(ordineData.awareness_seats);
          if (seats > seatsBase && awrProd.scalabile) {
            const blocchi = Math.ceil((seats - seatsBase) / (awrProd.incremento_scalabilita || 10));
            totale += blocchi * parseFloat(awrProd.prezzo_scalabilita || awrProd.prezzo_base);
          }
        }
      }
      // MSSP
      if (ordineData.servizio_mssp_monitoring) {
        const msspProd = listinoData?.find(p => p.tipo_prodotto === 'MSSP_MONITORING' && p.attivo);
        if (msspProd) {
          const gettoniBase = msspProd.numero_gettoni || 20;
          const gettoni = parseInt(ordineData.gettoni_totali) || gettoniBase;
          const prezzoPerGett = parseFloat(msspProd.prezzo_base) / gettoniBase;
          totale += prezzoPerGett * gettoni;
        }
      }
    }

    return totale;
  };

  const prezzo = calcolaPrezzo();
  const iva = prezzo * 0.22;
  const totaleConIva = prezzo + iva;

  const fieldError = (field) => {
    const err = errors?.[field];
    if (!err) return null;
    const msg = Array.isArray(err) ? err[0] : err;
    return <p className="text-red-500 text-xs mt-1">{msg}</p>;
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center gap-3 mb-4">
        <ClipboardCheck className="w-6 h-6 text-yellow-600" />
        <h2 className="text-xl font-bold text-gray-900">Riepilogo Ordine</h2>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto pr-2">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Colonna sinistra */}
          <div className="space-y-4">
            {/* Azienda */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-800 mb-2 text-sm uppercase tracking-wide">
                Azienda
              </h3>
              <dl className="text-sm space-y-1">
                <div className="flex justify-between">
                  <dt className="text-gray-500">Nome</dt>
                  <dd className="font-medium">{aziendaData.nome_azienda || '—'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Email</dt>
                  <dd className="font-medium">{aziendaData.email || '—'}</dd>
                </div>
                {aziendaData.partita_iva && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500">P.IVA</dt>
                    <dd className="font-medium">{aziendaData.partita_iva}</dd>
                  </div>
                )}
                {aziendaData.citta && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Città</dt>
                    <dd className="font-medium">{aziendaData.citta}</dd>
                  </div>
                )}
                {isCreatingNew && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Tipo</dt>
                    <dd className="font-medium text-yellow-700">Nuova azienda</dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Prodotto */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-800 mb-2 text-sm uppercase tracking-wide">
                Prodotto
              </h3>
              <dl className="text-sm space-y-1">
                <div className="flex justify-between">
                  <dt className="text-gray-500">Prodotto</dt>
                  <dd className="font-medium">{prodotto?.nome_prodotto || '—'}</dd>
                </div>
                {isBundle && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Tipo</dt>
                    <dd className="font-medium text-yellow-700">
                      <Package size={14} className="inline mr-1" />Bundle
                    </dd>
                  </div>
                )}
                <div className="flex justify-between">
                  <dt className="text-gray-500">Endpoint</dt>
                  <dd className="font-medium">{ordineData.numero_endpoint}</dd>
                </div>
                {ordineData.offerta_sorveglianza_rete && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500"><Wifi size={14} className="inline mr-1" />Network Surveillance</dt>
                    <dd className="font-medium text-green-600">✓ Incluso</dd>
                  </div>
                )}
                {ordineData.awareness_enabled && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500"><Eye size={14} className="inline mr-1" />Awareness</dt>
                    <dd className="font-medium">{ordineData.awareness_seats} utenze</dd>
                  </div>
                )}
                {ordineData.servizio_mssp_monitoring && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500"><Monitor size={14} className="inline mr-1" />Monitoraggio Cyber</dt>
                    <dd className="font-medium">{ordineData.gettoni_totali} gettoni</dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Asset */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-800 mb-2 text-sm uppercase tracking-wide">
                Asset
              </h3>
              <ul className="text-sm space-y-1">
                {(ordineData.asset || []).filter(a => a.asset?.trim()).map((a, i) => (
                  <li key={i} className="flex justify-between">
                    <span className="text-gray-500 capitalize">{a.tipo_asset}</span>
                    <span className="font-medium">{a.asset}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Colonna destra: Prezzo */}
          <div>
            <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-lg p-5 border border-yellow-200">
              <h3 className="font-semibold text-gray-800 mb-3 text-sm uppercase tracking-wide flex items-center gap-2">
                <Shield size={16} className="text-yellow-600" />
                Riepilogo Prezzo
              </h3>
              <dl className="text-sm space-y-2">
                <div className="flex justify-between">
                  <dt className="text-gray-600">Imponibile</dt>
                  <dd className="font-semibold">€{prezzo.toFixed(2)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-600">IVA (22%)</dt>
                  <dd className="font-medium">€{iva.toFixed(2)}</dd>
                </div>
                <hr className="border-yellow-300" />
                <div className="flex justify-between text-lg">
                  <dt className="font-bold text-gray-800">Totale</dt>
                  <dd className="font-bold text-yellow-700">€{totaleConIva.toFixed(2)}</dd>
                </div>
              </dl>
            </div>

            {fieldError('detail')}
          </div>
        </div>
      </div>
    </div>
  );
}
